<template>
    <div class="space-y-6">
        <div class="flex space-x-6" v-if="templateCategories.length">
            <nio-input-group class="flex-1" :label="$t('appointments.sendConfirmationModal.templateCategory')">
                <nio-select :options="templateCategoryOptions" v-model="selectedCategory" />
            </nio-input-group>
            <nio-input-group class="flex-1" :label="$t('appointments.sendConfirmationModal.template')">
                <nio-select :disabled="!selectedCategory" :options="templateOptions" v-model="selectedTemplate" />
            </nio-input-group>
        </div>

        <!-- TODO: Merge w/ TemplateModal.vue -->
        <nio-input-group :label="$t('automations.modal.subject')" v-if="showSubject">
            <template #after-label>
                <language-chooser class="link-gray | ml-2" :locales="locales" v-model="activeLocale" />
            </template>
            <template #right-label>
                <attributes-dropdown :additional-attributes="additionalAttributes" @insert="insertAttributeInSubject" />
            </template>
            <nio-input
                :placeholder="$t('automations.modal.subject')"
                ref="subject"
                v-model="subjects[activeLocale]"
            />
        </nio-input-group>

        <nio-input-group :label="$t('automations.modal.message')">
            <template #after-label>
                <language-chooser class="link-gray | ml-2" :locales="locales" v-model="activeLocale" />
            </template>
            <template #right-label>
                <attributes-dropdown :additional-attributes="additionalAttributes" @insert="insertAttributeInMessage" />
            </template>
            <nio-textarea
                :placeholder="$t('chat.messageInput.enterMessage')"
                rows="3"
                ref="message"
                v-model="messages[activeLocale]"
            />
        </nio-input-group>

        <hr>

        <nio-input-group :label="$t('appointments.sendConfirmationModal.preview')">
            <div class="relative min-h-24 px-4 py-2 border border-gray-200 rounded bg-white" :class="{ 'rounded-b-none': hasMultipleSelectedItems }">
                <p v-html="messagePreview" />
                <message-segments-usage
                    class="absolute right-0 bottom-0 flex rounded-tl"
                    :class="{ 'rounded-br': !hasMultipleSelectedItems }"
                    :message="messagePreview"
                />
            </div>
            <nio-button-group class="w-full border border-t-0 border-gray-200 rounded-t-none shadow-none" v-if="hasMultipleSelectedItems">
                <nio-button
                    class="flex-1"
                    :disabled="prevButtonDisabled"
                    @click="previewIndex--"
                >
                    {{ $t('global.general.previous') }}
                </nio-button>

                <nio-button
                    class="flex-1"
                    :disabled="nextButtonDisabled"
                    @click="previewIndex++"
                >
                    {{ $t('global.general.next') }}
                </nio-button>
            </nio-button-group>
        </nio-input-group>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import { nl2br } from '@/utils/index.js';
    import MessageParsing from '@/mixins/MessageParsing.js';

    import AttributesDropdown from '@/components/AttributesDropdown.vue';
    import LanguageChooser from '@/components/LanguageChooser.vue';
    import MessageSegmentsUsage from '@/components/MessageSegmentsUsage.vue';

    export default {
        components: {
            AttributesDropdown,
            LanguageChooser,
            MessageSegmentsUsage,
        },

        mixins: [MessageParsing],

        props: {
            initialMessage: {
                type: Object,
                default: () => ({ fr: '', en: '' }),
            },
            initialSubject: {
                type: Object,
                default: () => ({ fr: '', en: '' }),
            },
            selectedItems: {
                type: Array,
                default: () => [],
            },
            showSubject: {
                type: Boolean,
                default: false,
            },
            appendedMessage: {
                validator: value => ['confirmation', 'payment'].includes(value),
                default: null,
            },
        },

        data() {
            return {
                messages: this.initialMessage,
                subjects: this.initialSubject,
                selectedCategory: null,
                selectedTemplate: null,
                activeLocale: 'fr',
                previewIndex: 0,
            };
        },

        computed: {
            ...mapState('chat', {
                templateCategories: state => state.templateCategories.filter(category => category.templates.length > 0),
            }),

            ...mapState('campaign', [
                'selectedCampaign',
            ]),

            additionalAttributes() {
                if (!this.selectedCampaign.placeholders) {
                    return [];
                }

                return this.selectedCampaign.placeholders.map(placeholder => placeholder.name);
            },

            templateCategoryOptions() {
                return this.templateCategories.map(category => {
                    return {
                        value: category.id,
                        label: category.name,
                    };
                });
            },

            templateOptions() {
                return this.templates.map(template => {
                    return {
                        value: template.id,
                        label: template.name,
                    };
                });
            },

            exampleAppointment() {
                return [
                    {
                        datetime: moment().format(),
                        contact: {
                            civility: 'mr',
                            first_name: 'John',
                            last_name: 'Doe',
                            locale: this.activeLocale,
                        },
                        contact_phone: {
                            number: '14501231234',
                        },
                        amount: 9999,
                        invoice_number: 'INV001',
                    },
                ];
            },

            messagePreview() {
                const previewObject = !empty(this.selectedItems) ? this.selectedItems[this.previewIndex] : this.exampleAppointment[this.previewIndex];
                const locale = this.getLocale(previewObject);
                let message = this.messages[locale];
                const appendedMessage = !this.showSubject ? this.appendedMessage : null;

                if (empty(message)) {
                    if (empty(this.messages.fr) && this.enEnabled) {
                        message = this.messages.en;
                    } else if (empty(this.messages.en) && this.frEnabled) {
                        message = this.messages.fr;
                    }
                }

                return nl2br(this.fillTemplateValues(message, previewObject, appendedMessage, null, false));
            },

            templates() {
                const category = this.templateCategories.find(tmpCategory => tmpCategory.id == this.selectedCategory);

                if (empty(category)) {
                    return [];
                }

                return category.templates;
            },

            frEnabled() {
                if (empty(this.selectedItems)) {
                    return true;
                }

                if (!empty(this.selectedItems[0].contact)) {
                    return this.selectedItems.find(a => a.contact.locale == 'fr');
                }

                return this.selectedItems.find(c => c.locale == 'fr');
            },

            enEnabled() {
                if (empty(this.selectedItems)) {
                    return true;
                }

                if (!empty(this.selectedItems[0].contact)) {
                    return this.selectedItems.find(a => a.contact.locale == 'en');
                }

                return this.selectedItems.find(c => c.locale == 'en');
            },

            nextButtonDisabled() {
                return empty(this.selectedItems) || this.previewIndex >= this.selectedItems.length - 1;
            },

            prevButtonDisabled() {
                return this.previewIndex <= 0;
            },

            locales() {
                const locales = [];

                if (this.frEnabled) {
                    locales.push({ value: 'fr', label: 'Français' });
                }

                if (this.enEnabled) {
                    locales.push({ value: 'en', label: 'English' });
                }

                return locales;
            },

            hasMultipleSelectedItems() {
                return this.selectedItems.length > 1;
            },
        },

        watch: {
            selectedTemplate() {
                const template = this.templates.find(tmpTemplate => tmpTemplate.id == this.selectedTemplate);

                if (!empty(template)) {
                    if (this.showSubject) {
                        this.subjects.fr = this.frEnabled ? template.subject.fr : '';
                        this.subjects.en = this.enEnabled ? template.subject.en : '';
                    }

                    this.messages.fr = this.frEnabled ? template.content.fr : '';
                    this.messages.en = this.enEnabled ? template.content.en : '';

                    this.$refs.message.focus();
                }
            },

            initialMessage(newValue) {
                if (newValue === this.messages) {
                    return;
                }

                this.messages.fr = this.initialMessage.fr;
                this.messages.en = this.initialMessage.en;
            },

            messages(newValue) {
                this.$emit('update:initial-message', newValue);
            },

            initialSubject(newValue) {
                if (newValue === this.subjects) {
                    return;
                }

                this.subjects.fr = this.initialSubject.fr;
                this.subjects.en = this.initialSubject.en;
            },

            subjects(newValue) {
                this.$emit('update:initial-subject', newValue);
            },

            frEnabled: {
                immediate: true,
                handler(newValue) {
                    if (this.activeLocale == 'fr' && !newValue) {
                        this.activeLocale = 'en';
                    }
                },
            },

            enEnabled: {
                immediate: true,
                handler(newValue) {
                    if (this.activeLocale == 'en' && !newValue) {
                        this.activeLocale = 'fr';
                    }
                },
            },
        },

        methods: {
            insertAttributeInMessage(attribute) {
                this.$refs.message.insertAtCaret(attribute);
            },

            insertAttributeInSubject(attribute) {
                this.$refs.subject.insertAtCaret(attribute);
            },

            reset() {
                this.messages.fr = '';
                this.messages.en = '';
                this.subjects.fr = '';
                this.subjects.en = '';
                this.selectedCategory = null;
                this.selectedTemplate = null;
                this.activeLocale = 'fr';
                this.previewIndex = 0;
            },

            getLocale(previewObject) {
                if (!empty(previewObject.contact)) {
                    return previewObject.contact.locale;
                } if (!empty(previewObject.locale)) {
                    return previewObject.locale;
                }

                return this.activeLocale;
            },

            focus() {
                this.$refs.message.focus();
            },
        },
    };
</script>
